import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { useEffect, useState } from "react";
import TextInput from "components/files/TextInput";
import { useSelector } from "react-redux";
import CustomIconButton from "components/CustomIconButton";
import { Add, Delete } from "@mui/icons-material";
import CustomCard from "components/CustomCard";
import { Stack, Typography, Divider, useMediaQuery, useTheme, IconButton } from "@mui/material";
import { useFieldArray } from "react-hook-form";
import { DEPARTMENT_MANAGER } from "config/roles";
import NullCheck from "components/NullCheck";
import { onlyNumber } from "utils/validations";
import { currencyFormat } from "config/functions";
import CustomDataGrid from "components/customDataGrid";

const SupplierCosts = ({ watch, control, data, errors, totalSupplierCosts }) => {
    const { bt } = useBizTypeTranslation();
    const user = useSelector((state) => state?.userSettings?.user);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { fields, append, remove, replace } = useFieldArray({ control, name: "supplierCosts", keyName: "fieldId", });
    const [fieldsLoad, setFieldsLoad] = useState(false);

    useEffect(() => {
        if (Array.isArray(data?.supplierCosts)) {
            replace(data?.supplierCosts);
        }
    }, [data?.supplierCosts]);

    useEffect(() => {
        setFieldsLoad(true);
        const timer = setTimeout(() => setFieldsLoad(false), 0.1);
        return () => clearTimeout(timer);
    }, [fields]);

    const addNew = () => {
        append({
            supplierName: "",
            itemName: "",
            qty: 1,
            price: "0",
            total: "0",
            notes: "",
            employeeName: `${user?.fn ?? ""} ${user?.ln ?? ""}`.trim(),
        });
    };

    return (
        <CustomCard width="100%">
            <Stack direction="row" spacing={1} mb={1} alignItems="center" justifyContent="space-between">
                <Typography fontSize={14}>{`${bt("SUPPLIER_COSTS")}: ${currencyFormat(totalSupplierCosts)}`}</Typography>
                <CustomIconButton hideBgcolor icon={<Add />} size={30} onClick={addNew} />
            </Stack>

            {isMobile
                ? fields?.map((field, index) => {
                    const total = watch(`supplierCosts.${index}.price`) * watch(`supplierCosts.${index}.qty`);
                    return (
                        <>
                            <CustomCard key={field.fieldId} width="100%" borderColor="#eaeaeaff">
                                <Stack direction="row" alignItems="center">
                                    <TextInput
                                        errors={errors}
                                        standard
                                        name={`supplierCosts.${index}.employeeName`}
                                        control={control}
                                        defaultValue={field.employeeName ?? `${user?.fn ?? ""} ${user?.ln ?? ""}`.trim()}
                                        readOnly
                                        title={bt("EMPLOYEE")}
                                    />

                                    <Typography fontSize={12} fontWeight="bold" sx={{ ml: 1 }}>
                                        {total ? `${bt("SUM")}: ${total.toFixed(2)}` : ""}
                                    </Typography>
                                </Stack>

                                <Divider />

                                <Stack direction={{ xs: 'column', md: "row" }} spacing={1}>
                                    <TextInput
                                        fullWidth
                                        control={control}
                                        errors={errors}
                                        required
                                        name={`supplierCosts.${index}.supplierName`}
                                        title="SUPPLIER_NAME"
                                    />
                                    <TextInput
                                        fullWidth
                                        control={control}
                                        errors={errors}
                                        required
                                        name={`supplierCosts.${index}.itemName`}
                                        title="ITEM_NAME"
                                    />

                                    <TextInput
                                        fullWidth
                                        control={control}
                                        errors={errors}
                                        validation={onlyNumber}
                                        min={1}
                                        required
                                        name={`supplierCosts.${index}.qty`}
                                        title="QTY"
                                    />

                                    <TextInput
                                        fullWidth
                                        control={control}
                                        errors={errors}
                                        validation={onlyNumber}
                                        required
                                        name={`supplierCosts.${index}.price`}
                                        title="PRICE"
                                    />
                                </Stack>
                            </CustomCard>
                            <Divider sx={{ borderColor: "#000" }} />
                        </>
                    );
                })
                : !fieldsLoad
                    ? <CustomDataGrid
                        width="100%"
                        getRowId={(row) => row.fieldId}
                        list={fields?.map((field, index) => ({
                            ...field,
                            index: index,
                            total: watch(`supplierCosts.${index}.price`) * watch(`supplierCosts.${index}.qty`),
                        })
                        )}
                        columns={[
                            {
                                field: 'employeeName',
                                headerName: bt("EMPLOYEE"),
                                width: 100,
                                renderCell: (params) =>
                                    <Stack height="100%" justifyContent="center" >
                                        <TextInput
                                            errors={errors}
                                            width={90}
                                            name={`works.${params.row.index}.employeeName`}
                                            control={control}
                                            defaultValue={params.value ?? `${user?.fn ?? ""} ${user?.ln ?? ""}`.trim()}
                                            readOnly
                                        />
                                    </Stack>
                            },
                            {
                                field: 'supplierName',
                                headerName: bt("SUPPLIER_NAME"),
                                width: 180,
                                renderCell: (params) =>
                                    <Stack height="100%" justifyContent="center" >
                                        <TextInput
                                            width={160}
                                            control={control}
                                            errors={errors}
                                            name={`supplierCosts.${params.row.index}.supplierName`}
                                        />
                                    </Stack>
                            },
                            {
                                field: 'itemName',
                                headerName: bt("ITEM_NAME"),
                                width: 180,
                                renderCell: (params) =>
                                    <Stack height="100%" justifyContent="center" >
                                        <TextInput
                                            width={160}
                                            control={control}
                                            errors={errors}
                                            name={`supplierCosts.${params.row.index}.itemName`}
                                        />
                                    </Stack>
                            },
                            {
                                field: 'qty',
                                headerName: bt("QTY"),
                                width: 100,
                                renderCell: (params) =>
                                    <Stack height="100%" justifyContent="center" >
                                        <TextInput
                                            width={90}
                                            control={control}
                                            errors={errors}
                                            name={`supplierCosts.${params.row.index}.qty`}
                                        />
                                    </Stack>
                            },
                            {
                                field: 'price',
                                headerName: bt("PRICE"),
                                width: 100,
                                renderCell: (params) =>
                                    <Stack height="100%" justifyContent="center" >
                                        <TextInput
                                            width={90}
                                            control={control}
                                            errors={errors}
                                            name={`supplierCosts.${params.row.index}.price`}
                                        />
                                    </Stack>
                            },
                            {
                                field: 'total',
                                headerName: bt("SUM"),
                                width: 100,
                                renderCell: (params) =>
                                    <Stack height="100%" justifyContent="center" >
                                        {params.row.total ? currencyFormat(params.row.total) : ""}
                                    </Stack>
                            },
                            // {
                            //     field: "delete",
                            //     headerName: bt("DELETE"),
                            //     width: 90,
                            //     filterable: false,
                            //     hide: !DEPARTMENT_MANAGER.includes(user?.userRole),
                            //     renderCell: (params) => {
                            //         return (
                            //             <IconButton onClick={(e) => {
                            //                 e.preventDefault();
                            //                 remove(params.row.index)
                            //             }}>
                            //                 <Delete />
                            //             </IconButton>
                            //         );
                            //     },
                            // },
                        ]}
                        hideFooter
                    />
                    : null
            }

            <NullCheck valueForCheck={fields?.length === 0}>
                <Typography fontWeight='bold' textAlign='center' >{bt("NO_SUPPLIER_COSTS")}</Typography>
            </NullCheck>

        </CustomCard>
    );
};

export default SupplierCosts;
